<div class="hidden-general-dialog">
  <!-- Header -->
  <div
    class="flex items-start sm:items-center justify-between !px-4 !py-3 sm:!py-2 mb-2 border-b border-slate-100 gap-3">
    <h2 mat-dialog-title class="!m-0 !p-0 text-[18px] font-bold text-slate-800 leading-tight flex-1">
      <ng-container [ngSwitch]="step">
        <span *ngSwitchCase="'confirm'">Switch these employees to Employee Controlled?</span>
        <span *ngSwitchCase="'processing'">Updating employees…</span>
        <span *ngSwitchDefault>Next steps for your team</span>
      </ng-container>
    </h2>
    <button mat-icon-button class="text-slate-400 hover:text-slate-600 transition-colors shrink-0 -mt-1 sm:mt-0"
      (click)="onCancel()" aria-label="Close dialog" [disabled]="step === 'processing'">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <mat-dialog-content class="dialog-content">
    <ng-container *ngIf="step === 'confirm'">
      <!-- General warning -->
      <div class="flex-col p-4 rounded-xl mb-5 mt-2 bg-[#fffbeb] border border-solid border-[#fde68a] shadow-sm">
        <div class="flex gap-3 items-start w-full">
          <mat-icon class="text-[#d97706] shrink-0 mt-0.5">info</mat-icon>
          <div class="flex flex-col gap-1.5">
            <p class="text-[#92400e] text-[14px] font-medium !mb-0 !mt-0 leading-snug tracking-[inherit]">
              This will reset passwords for <strong>{{ employeeCount }}</strong> Hidden Mode employee{{ employeeCount
              === 1 ? '' : 's' }} and sign them out. Screenshots and activity history
              stay in SuperSee.
            </p>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-2 gap-2 mb-5 text-[13px]">
        <div class="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <p class="!m-0 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Now</p>
          <p class="!mb-0 !mt-1 text-slate-700">Hidden mode. They don't sign in.</p>
        </div>
        <div class="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
          <p class="!m-0 text-[11px] font-semibold uppercase tracking-wide text-amber-800">After</p>
          <p class="!mb-0 !mt-1 text-amber-900">Employee Controlled. They sign in with a new password.</p>
        </div>
      </div>

      <!-- Affected employees -->
      <div class="flex flex-col gap-2 mb-5" *ngIf="data.employees.length">
        <p class="text-[13.5px] font-medium text-slate-600 !m-0">Affected employees ({{ employeeCount }})</p>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 overflow-y-auto max-h-[180px] pr-1">
          <div
            class="flex items-center gap-2.5 px-3 py-2.5 rounded-lg border border-solid border-[#e5e7eb] bg-[#f6f7f9] transition-all hover:border-gray-300 cursor-default"
            *ngFor="let emp of data.employees" [matTooltip]="getEmployeeTooltip(emp)" matTooltipPosition="above">
            <div class="flex-shrink-0 flex items-center justify-center rounded-full overflow-hidden w-[32px] h-[32px]">
              <ngx-avatars name="{{ emp.name }}" size="32" initialsSize="2"></ngx-avatars>
            </div>
            <p class="!text-[13.5px] font-medium text-slate-700 truncate m-0">{{ emp.name }}</p>
          </div>
        </div>
      </div>

      <ol
        class="flex flex-col gap-2 pl-5 mb-5 text-[14px] text-slate-600 list-decimal marker:text-slate-400 marker:font-medium">
        <li>We reset each password and disable Hidden Mode.</li>
        <li>Download the Employee Controlled installer for each OS.</li>
        <li>Share the new password (next screen).</li>
        <li>Employees install and sign in with email and the new password.</li>
      </ol>

      <p class="text-[13px] text-slate-500 !mb-0 !mt-0 leading-relaxed text-center">
        After this, return to the downgrade review — the Hidden Mode warning should clear once users are migrated.
      </p>

      <p class="text-[13px] text-red-500 font-medium text-center mt-3" *ngIf="errorMessage">{{ errorMessage }}</p>
    </ng-container>

    <ng-container *ngIf="step === 'processing'">
      <div class="flex flex-col items-center justify-center py-10 gap-4">
        <mat-spinner diameter="42"></mat-spinner>
        <p class="text-[15px] font-medium text-slate-700 m-0">Resetting passwords and updating {{ employeeCount }}
          employee account{{ employeeCount === 1 ? '' : 's' }}…</p>
      </div>
    </ng-container>

    <ng-container *ngIf="step === 'result'">
      <div class="flex-col p-4 rounded-xl mb-5 mt-2 bg-[#ecfdf5] border border-solid border-[#a7f3d0] shadow-sm"
        *ngIf="successfulResults.length">
        <div class="flex gap-3 items-start w-full">
          <mat-icon class="text-[#10b981] shrink-0 mt-0.5">check_circle</mat-icon>
          <div class="flex flex-col gap-1.5">
            <p class="text-[#065f46] text-[14px] font-medium !mb-0 !mt-0 leading-snug tracking-[inherit]">
              {{ successfulResults.length }} employee{{ successfulResults.length === 1 ? '' : 's' }} switched to
              Employee Controlled. Share the new passwords below, then install.
            </p>
          </div>
        </div>
      </div>

      <div class="flex-col p-4 rounded-xl mb-5 mt-2 bg-[#fffbeb] border border-solid border-[#fde68a] shadow-sm"
        *ngIf="failedResults.length">
        <div class="flex gap-3 items-start w-full">
          <mat-icon class="text-[#d97706] shrink-0 mt-0.5">warning</mat-icon>
          <div class="flex flex-col gap-1.5">
            <p class="text-[#92400e] text-[14px] font-medium !mb-0 !mt-0 leading-snug tracking-[inherit]">
              {{ failedResults.length }} employee{{ failedResults.length === 1 ? '' : 's' }} could not be updated. Retry from Employee Settings.
            </p>
          </div>
        </div>
      </div>

      <div class="flex flex-col mb-5" *ngIf="successfulResults.length">
        <div class="flex items-center justify-between mb-0">
          <h3 class="!text-slate-800 !mb-0 !mt-0 text-[16px] font-semibold leading-tight">New sign-in credentials</h3>
          <button mat-button type="button" color="primary" class="!text-indigo-600 !font-medium"
            (click)="copyAllCredentials()">
            Copy all
          </button>
        </div>
        <div class="flex flex-col gap-3">
          <div class="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg"
            *ngFor="let result of successfulResults">
            <div class="flex flex-col gap-1.5">
              <strong class="text-[14px] text-slate-800 mb-0.5">{{ result.name }}</strong>
              <div class="flex items-center gap-2">
                <span class="text-[13px] font-medium text-slate-500 w-[60px]">Email</span>
                <span class="text-[13px] text-slate-800">{{ result.email }}</span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-[13px] font-medium text-slate-500 w-[60px]">Password</span>
                <code
                  class="text-[13px] text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100 w-max font-mono">{{ result.password }}</code>
              </div>
            </div>
            <button mat-icon-button type="button" class="text-slate-400 hover:text-indigo-600 transition-colors"
              (click)="copyCredentials(result)" aria-label="Copy credentials">
              <mat-icon>content_copy</mat-icon>
            </button>
          </div>
        </div>
      </div>

      <div class="flex flex-col mb-5" *ngIf="successfulResults.length">
        <h3 class="!text-slate-800 !mb-1 !mt-0 text-[16px] font-semibold leading-tight">Download Employee controlled agent</h3>
        <p class="text-[13px] text-slate-500 !mb-4 !mt-0 leading-relaxed">
          Employee Controlled app — employees sign in with their work email and the new password above.
        </p>
        <div class="flex flex-wrap gap-3 mt-1">
          <button mat-stroked-button color="primary" type="button" *ngFor="let item of generalDownloads"
            class="w-full sm:w-auto !border-indigo-200 !text-indigo-700 !font-medium hover:!bg-indigo-50 transition-colors"
            (click)="item.action()">
            <span class="whitespace-normal leading-tight block">{{ item.label }}</span>
          </button>
        </div>
      </div>

      <ul class="flex flex-col gap-2 mb-0">
        <li class="flex items-start gap-2.5 text-[13.5px] text-slate-600">
          <span class="w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0 mt-1.5"></span>
          <span>Distribute the Employee controlled agent installer and new password to each employee.</span>
        </li>
        <li class="flex items-start gap-2.5 text-[13.5px] text-slate-600">
          <span class="w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0 mt-1.5"></span>
          <span>Ask them to uninstall or stop the old Hidden agent if it is still running.</span>
        </li>
        <li class="flex items-start gap-2.5 text-[13.5px] text-slate-600">
          <span class="w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0 mt-1.5"></span>
          <span>After sign-in, monitoring continues — no historical data is lost.</span>
        </li>
      </ul>
    </ng-container>
  </mat-dialog-content>

  <mat-dialog-actions
    class="flex flex-col-reverse sm:flex-row w-full justify-center sm:!justify-end gap-3 pt-4 pb-2 border-t border-slate-100 mt-2 !mb-0 px-4">
    <ng-container [ngSwitch]="step">
      <ng-container *ngSwitchCase="'confirm'">
        <button mat-stroked-button type="button" class="action-secondary w-full sm:w-auto min-h-[40px] !m-0"
          (click)="onCancel()">Cancel</button>
        <button mat-raised-button color="primary" type="button"
          class="action-primary w-full sm:w-auto min-h-[40px] !m-0" (click)="onConfirm()">
          Reset passwords &amp; switch
        </button>
      </ng-container>
      <ng-container *ngSwitchCase="'result'">
        <button mat-raised-button color="primary" type="button"
          class="action-primary w-full sm:w-auto min-h-[40px] !m-0" (click)="onDone()">Done</button>
      </ng-container>
    </ng-container>
  </mat-dialog-actions>
</div>